import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  CircularProgress,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { createAdminUser } from '../services/api';

const COURSES = [
  "Artificial Intelligence (Generative AI & Agentic AI)",
  "Full Stack Digital Marketing & E-Commerce",
  "Full Stack Web Development",
  "Ethical Hacking",
];

const QUALIFICATIONS = [
  { value: "matric", label: "Matric" },
  { value: "intermediate", label: "Intermediate" },
  { value: "bachelor", label: "Bachelor / Higher" },
];

function AddUser() {
  const [formData, setFormData] = useState({
    registrationType: 'online', // 'online' or 'onsite'
    fullName: '',
    fatherName: '',
    cnic: '',
    phone: '',
    email: '',
    city: 'Lahore',
    qualification: 'bachelor',
    courseApplied: COURSES[0],
    gender: 'male',
    dob: '',
    address: '',
    challanOption: 'auto', // 'auto' or 'custom'
    customChallanId: '',
    amount: '500',
    isPaid: 'false', // 'true' or 'false'
    password: '',
  });

  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMsg('');
    setErrorMsg('');

    try {
      const payload = {
        ...formData,
        isPaid: formData.isPaid === 'true',
      };
      
      const response = await createAdminUser(payload);
      if (response.success) {
        setSuccessMsg(response.message || 'User and Challan created successfully!');
        // Reset form
        setFormData({
          registrationType: 'online',
          fullName: '',
          fatherName: '',
          cnic: '',
          phone: '',
          email: '',
          city: 'Lahore',
          qualification: 'bachelor',
          courseApplied: COURSES[0],
          gender: 'male',
          dob: '',
          address: '',
          challanOption: 'auto',
          customChallanId: '',
          amount: '500',
          isPaid: 'false',
          password: '',
        });
      } else {
        setErrorMsg(response.message || 'Failed to create user.');
      }
    } catch (err: any) {
      console.error('Error creating user:', err);
      setErrorMsg(err.response?.data?.message || 'Server error. Please verify the input values.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <PersonAddIcon color="primary" sx={{ fontSize: 40 }} />
        <Typography variant="h4" fontWeight="bold">
          Add New Student & Challan
        </Typography>
      </Box>

      {successMsg && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMsg}
        </Alert>
      )}

      {errorMsg && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMsg}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: 4, mb: 3 }} elevation={1}>
          <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
            1. Registration Type
          </Typography>
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <RadioGroup
              row
              name="registrationType"
              value={formData.registrationType}
              onChange={handleChange}
            >
              <FormControlLabel value="online" control={<Radio />} label="Online Classes Student" />
              <FormControlLabel value="onsite" control={<Radio />} label="Onsite Classes Student (Lahore)" />
            </RadioGroup>
          </FormControl>

          <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
            2. Personal Details
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                label="Full Name"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                label="Father's Name"
                name="fatherName"
                value={formData.fatherName}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                label="CNIC / B-Form Number"
                name="cnic"
                placeholder="e.g. 35201-1234567-1"
                value={formData.cnic}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                label="Mobile Number"
                name="phone"
                placeholder="e.g. 0300-1234567"
                value={formData.phone}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                type="email"
                label="Email Address"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                type="date"
                label="Date of Birth"
                name="dob"
                value={formData.dob}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Gender</InputLabel>
                <Select
                  name="gender"
                  value={formData.gender}
                  label="Gender"
                  onChange={handleChange}
                >
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                label="City"
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                required
                fullWidth
                label="Complete Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 4, mb: 3 }} elevation={1}>
          <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
            3. Academic & Course Selection
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Qualification</InputLabel>
                <Select
                  name="qualification"
                  value={formData.qualification}
                  label="Qualification"
                  onChange={handleChange}
                >
                  {QUALIFICATIONS.map((q) => (
                    <MenuItem key={q.value} value={q.value}>{q.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Course</InputLabel>
                <Select
                  name="courseApplied"
                  value={formData.courseApplied}
                  label="Course"
                  onChange={handleChange}
                >
                  {COURSES.map((course) => (
                    <MenuItem key={course} value={course}>{course}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {formData.registrationType === 'online' && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Custom Portal Password (Optional)"
                  name="password"
                  value={formData.password}
                  placeholder="Defaults to CNIC digits"
                  onChange={handleChange}
                  helperText="Leave empty to automatically generate a login password based on the student's CNIC digits"
                />
              </Grid>
            )}
          </Grid>
        </Paper>

        <Paper sx={{ p: 4, mb: 3 }} elevation={1}>
          <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
            4. Challan & Payment Info
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl component="fieldset" fullWidth>
                <Typography variant="body2" sx={{ mb: 1 }}>Challan Number Option</Typography>
                <RadioGroup
                  row
                  name="challanOption"
                  value={formData.challanOption}
                  onChange={handleChange}
                >
                  <FormControlLabel value="auto" control={<Radio />} label="Auto-Generate" />
                  <FormControlLabel value="custom" control={<Radio />} label="Custom Number" />
                </RadioGroup>
              </FormControl>
            </Grid>
            {formData.challanOption === 'custom' && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  required
                  fullWidth
                  label="Custom Challan Number"
                  name="customChallanId"
                  value={formData.customChallanId}
                  onChange={handleChange}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                type="number"
                label="Challan Amount (Rs.)"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Payment Status</InputLabel>
                <Select
                  name="isPaid"
                  value={formData.isPaid}
                  label="Payment Status"
                  onChange={handleChange}
                >
                  <MenuItem value="false">Unpaid</MenuItem>
                  <MenuItem value="true">Paid</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        <Box display="flex" justifyContent="flex-end" mb={5}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <PersonAddIcon />}
            sx={{ px: 5, py: 1.5, fontWeight: 'bold' }}
          >
            {loading ? 'Adding User...' : 'Add Student & Challan'}
          </Button>
        </Box>
      </form>
    </Box>
  );
}

export default AddUser;
